import { t } from "@hakili/i18n";
import { useEffect, useState } from "react";

/** `C5.0.0(b)`: how long a shown link stays on the screen before hiding itself. */
export const LINK_SHOWN_MS = 20_000;

/**
 * `round C5.0`, `C5.0.0(b)`, made one component by `round C7`, `C7.3`: **a
 * link is a credential.** Hidden by default, copied without being shown,
 * shown only on « Afficher le lien », and hidden again on its own after
 * `LINK_SHOWN_MS`, so a screen left open does not keep it printed. The tour
 * sheet's card and the pièces card both render this, so the rule lives once.
 */
export function CopyableLink({
  link,
  testId,
  title,
  text,
}: {
  readonly link: string;
  readonly testId: string;
  readonly title: string;
  readonly text: string;
}): React.JSX.Element {
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!shown) return undefined;
    const timer = setTimeout(() => {
      setShown(false);
    }, LINK_SHOWN_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [shown]);

  return (
    <div
      data-testid={testId}
      className="mt-2 rounded-lg border border-dashed border-indigo-200 bg-white px-3 py-2"
    >
      <p className="text-xs font-medium text-gray-700">{title}</p>
      <p className="mt-1 text-xs leading-relaxed text-gray-500">{text}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {shown ? (
          <code
            data-testid={`${testId}-texte`}
            className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-700"
          >
            {link}
          </code>
        ) : null}
        <button
          type="button"
          data-testid={`${testId}-copier`}
          onClick={() => {
            void navigator.clipboard.writeText(link).then(() => {
              setCopied(true);
            });
          }}
          className="text-xs font-medium text-indigo-600"
        >
          {copied ? t("console.expertises.lienCopie") : t("console.expertises.lienCopier")}
        </button>
        <button
          type="button"
          data-testid={`${testId}-afficher`}
          onClick={() => {
            setShown(!shown);
          }}
          className="text-xs font-medium text-gray-500"
        >
          {shown ? t("console.expertises.lienMasquer") : t("console.expertises.lienAfficher")}
        </button>
      </div>
    </div>
  );
}
